import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { adminPath, isAdminSubdomain } from './utils/adminPaths';
import PublicLayout from './components/PublicLayout';
import AdminLayout from './components/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingScreen from './components/LoadingScreen';
import WelcomePopup from './components/WelcomePopup';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { LoadingProvider, RouteChangeHandler } from './context/LoadingContext';
import { ToastProvider } from './components/Toast';
import UpdateNotification from './pwa/UpdateNotification';

import OfflineIndicator from './pwa/OfflineIndicator';
import './App.css';
import './styles/rtl.css';
import './styles/responsive.css';

if (typeof window !== 'undefined') {
  const isStandalone = window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  if (isStandalone) {
    document.documentElement.classList.add('pwa-standalone');
  }
}

const Home = lazy(() => import('./pages/Home'));
const ProductList = lazy(() => import('./pages/ProductList'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const PaymentCallback = lazy(() => import('./pages/PaymentCallback'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Login = lazy(() => import('./pages/Login'));

const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Categories = lazy(() => import('./pages/admin/Categories'));
const Brands = lazy(() => import('./pages/admin/Brands'));
const News = lazy(() => import('./pages/admin/News'));
const PageImages = lazy(() => import('./pages/admin/PageImages'));
const Users = lazy(() => import('./pages/admin/Users'));
const Products = lazy(() => import('./pages/admin/Products'));
const Orders = lazy(() => import('./pages/admin/Orders'));
const ContactSubmissions = lazy(() => import('./pages/admin/ContactSubmissions'));
const ShippingCities = lazy(() => import('./pages/admin/ShippingCities'));
const PromoCodes = lazy(() => import('./pages/admin/PromoCodes'));
const ShippingSettings = lazy(() => import('./pages/admin/ShippingSettings'));
const DashboardModules = lazy(() => import('./pages/admin/DashboardModules'));
const AnnouncementBar = lazy(() => import('./pages/admin/AnnouncementBar'));
const PopupSettings = lazy(() => import('./pages/admin/PopupSettings'));
const AboutPageSettings = lazy(() => import('./pages/admin/AboutPageSettings'));
const ContactSettings = lazy(() => import('./pages/admin/ContactSettings'));
const Insights = lazy(() => import('./pages/admin/Insights'));

const AdminRoute = ({ children }) => (
  <ProtectedRoute>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <LanguageProvider>
    <LoadingProvider>
      <AuthProvider>
        <CartProvider>
          <ToastProvider>
          <OfflineIndicator />
          <UpdateNotification />
          <Router>
            <RouteChangeHandler />
            <WelcomePopup />
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                {!isAdminSubdomain && (
                  <>
                    <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                    <Route path="/products" element={<PublicLayout><ProductList /></PublicLayout>} />
                    <Route path="/products/:id" element={<PublicLayout><ProductDetail /></PublicLayout>} />
                    <Route path="/cart" element={<PublicLayout><Cart /></PublicLayout>} />
                    <Route path="/checkout" element={<PublicLayout><Checkout /></PublicLayout>} />
                    <Route path="/payment/callback" element={<PublicLayout><PaymentCallback /></PublicLayout>} />
                    <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
                    <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                  </>
                )}

                <Route path={adminPath('/login')} element={<Login />} />

                <Route path={adminPath('/')} element={<AdminRoute><Dashboard /></AdminRoute>} />
                <Route path={adminPath('/categories')} element={<AdminRoute><Categories /></AdminRoute>} />
                <Route path={adminPath('/brands')} element={<AdminRoute><Brands /></AdminRoute>} />
                <Route path={adminPath('/news')} element={<AdminRoute><News /></AdminRoute>} />
                <Route path={adminPath('/page-images')} element={<AdminRoute><PageImages /></AdminRoute>} />
                <Route path={adminPath('/users')} element={<AdminRoute><Users /></AdminRoute>} />
                <Route path={adminPath('/products')} element={<AdminRoute><Products /></AdminRoute>} />
                <Route path={adminPath('/orders')} element={<AdminRoute><Orders /></AdminRoute>} />
                <Route path={adminPath('/contact-submissions')} element={<AdminRoute><ContactSubmissions /></AdminRoute>} />
                <Route path={adminPath('/shipping-cities')} element={<AdminRoute><ShippingCities /></AdminRoute>} />
                <Route path={adminPath('/promo-codes')} element={<AdminRoute><PromoCodes /></AdminRoute>} />
                <Route path={adminPath('/shipping-settings')} element={<AdminRoute><ShippingSettings /></AdminRoute>} />
                <Route path={adminPath('/dashboard-modules')} element={<AdminRoute><DashboardModules /></AdminRoute>} />
                <Route path={adminPath('/announcement-bar')} element={<AdminRoute><AnnouncementBar /></AdminRoute>} />
                <Route path={adminPath('/popup-settings')} element={<AdminRoute><PopupSettings /></AdminRoute>} />
                <Route path={adminPath('/about-page-settings')} element={<AdminRoute><AboutPageSettings /></AdminRoute>} />
                <Route path={adminPath('/contact-settings')} element={<AdminRoute><ContactSettings /></AdminRoute>} />
                <Route path={adminPath('/insights')} element={<AdminRoute><Insights /></AdminRoute>} />

                {isAdminSubdomain ? (
                  <Route path="*" element={<Navigate to={adminPath('/')} replace />} />
                ) : (
                  // Public 404 (header + footer kept). Admin routes above still
                  // match first; only truly unknown URLs land here.
                  <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
                )}
              </Routes>
            </Suspense>
          </Router>
          </ToastProvider>
        </CartProvider>
      </AuthProvider>
    </LoadingProvider>
    </LanguageProvider>
  );
}

export default App;
